const META_IN_APP_BROWSER = /FBAN|FBAV|FBIOS|FB4A|FB_IAB|Instagram/i;

/**
 * Uses the regular cart checkout flow in Meta in-app browsers, where Shopify's
 * native product accelerated checkout can intermittently navigate to a blank
 * page. Other browsers continue to use Shopify's native accelerated button.
 */
class MetaAcceleratedCheckout extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button;

  /** @type {HTMLElement | null} */
  #error;

  connectedCallback() {
    if (!META_IN_APP_BROWSER.test(navigator.userAgent)) return;

    this.#button = this.querySelector('[data-meta-accelerated-checkout-button]');
    this.#error = this.querySelector('[data-meta-accelerated-checkout-error]');

    if (!this.#button) return;

    // Only replace the native button after the fallback is ready. If this
    // module cannot load, Shopify's native button remains available.
    this.querySelector('[data-native-accelerated-checkout]')?.setAttribute('hidden', '');
    this.#button.removeAttribute('hidden');
    this.#button.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = async () => {
    const form = this.closest('form');
    if (!(form instanceof HTMLFormElement) || !this.#button) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#setError('');
    this.#setBusy(true);

    try {
      const formData = new FormData(form);
      this.#usePendingMainProductVariant(form, formData);

      if (!formData.get('id')) {
        throw new Error('Please select an available option.');
      }

      const response = await fetch(this.dataset.cartAddUrl || form.action, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
        credentials: 'same-origin',
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This item could not be added to the cart.');
      }

      this.#submitRegularCheckout();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Checkout could not be opened. Please try again.';
      this.#setError(message);
      this.#setBusy(false);
    }
  };

  /**
   * Horizon resolves a variant with a section request. If a buyer taps the
   * fallback before that request finishes, the URL already contains the
   * intended main-product variant while the hidden input can still be stale.
   * Do not use the page URL for featured products or product cards.
   *
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   */
  #usePendingMainProductVariant(form, formData) {
    const productForm = form.closest('product-form-component');
    const productUrl = productForm?.dataset.productUrl;
    if (!productUrl) return;

    const productPath = new URL(productUrl, window.location.origin).pathname;
    if (productPath !== window.location.pathname) return;

    const variantId = new URL(window.location.href).searchParams.get('variant');
    if (variantId && /^\d+$/.test(variantId)) {
      formData.set('id', variantId);
    }
  }

  #submitRegularCheckout() {
    const checkoutForm = document.createElement('form');
    checkoutForm.method = 'post';
    checkoutForm.action = this.dataset.cartUrl || '/cart';

    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'Check out';

    checkoutForm.appendChild(checkoutInput);
    document.body.appendChild(checkoutForm);
    checkoutForm.submit();
  }

  /** @param {boolean} isBusy */
  #setBusy(isBusy) {
    if (!this.#button) return;

    this.#button.disabled = isBusy;
    this.#button.setAttribute('aria-busy', isBusy.toString());
    this.#button.textContent = isBusy ? 'Opening checkout…' : this.#button.dataset.defaultLabel || 'Buy it now';
  }

  /** @param {string} message */
  #setError(message) {
    if (!this.#error) return;

    this.#error.textContent = message;
    this.#error.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('meta-accelerated-checkout')) {
  customElements.define('meta-accelerated-checkout', MetaAcceleratedCheckout);
}
